import { FC, useEffect, useState } from 'react'
import type { FormInstance } from 'antd/es/form/Form'
import FormItem from 'antd/es/form/FormItem'
import { useWatch } from 'antd/es/form/Form'
import { CheckBoxLabel } from '@UI/CheckBox'
import InfoComp from '@pages/Admin/CRMSettings/components/InfoComp/InfoComp'
import SliderField from '@pages/Admin/CourseSettings/components/SliderField/SliderField'
import { RequestCreateLeadPaymentLink } from '@/api/types/crm/crm.types'
import spclasses from '../../../SetCourseClassDirectionModal/purchase.module.css'
import { CREATE_LINK_BLOCK_TARIFF_TYPES } from '../../tariffTypes'
import { withCreateLinkBlockTariffTypesHint } from '../../createLinkBlockInfoContent'

type FieldType = Omit<RequestCreateLeadPaymentLink, 'lead_funnel_id'>

type LessonCountSliderFieldProps = {
  value?: number | null
  onChange?: (value: number) => void
}

const LessonCountSliderField = ({
  value,
  onChange,
}: LessonCountSliderFieldProps) => {
  const normalizedValue = Math.max(1, Math.min(100, Math.trunc(Number(value) || 1)))

  return (
    <SliderField
      label="Количество занятий"
      value={normalizedValue}
      extraContent={normalizedValue.toString()}
      min={1}
      max={100}
      onChange={(next) => onChange?.(next)}
    />
  )
}

type LessonCountLinkFieldsProps = {
  form: FormInstance
}

/**
 * Кол-во занятий для hour / minute / group_per_lesson:
 * слайдер (предзаполнение value) + чекбокс is_value_fixed.
 */
const LessonCountLinkFields: FC<LessonCountLinkFieldsProps> = ({ form }) => {
  const isValueFixedWatch = useWatch('is_value_fixed', form) as boolean | null
  const [isFixed, setIsFixed] = useState(Boolean(isValueFixedWatch))

  useEffect(() => {
    setIsFixed(Boolean(isValueFixedWatch))
  }, [isValueFixedWatch])

  const onToggleFixed = (checked?: boolean) => {
    const next = typeof checked === 'boolean' ? checked : !isFixed
    setIsFixed(next)
    const currentValue = Number(form.getFieldValue('value'))
    form.setFieldsValue({
      is_value_fixed: next,
      value:
        Number.isFinite(currentValue) && currentValue >= 1
          ? Math.trunc(currentValue)
          : 1,
      // Фиксация → цена вручную → скидка недоступна.
      ...(next ? { discount: null, apply_discount_each_payment: false } : {}),
    })
  }

  return (
    <div className={spclasses.inputWithLabel}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <CheckBoxLabel
          type="button"
          value={isFixed}
          onClick={onToggleFixed}
          label="Зафиксировать кол-во занятий для оплаты"
          className={spclasses.checkboxWithLabel}
          style={{
            justifyContent: 'flex-start',
            paddingTop: '4px',
            paddingBottom: '4px',
            height: 'auto',
          }}
        />
        <InfoComp
          text={withCreateLinkBlockTariffTypesHint(
            'По умолчанию клиент при покупке сам выбирает количество занятий (слайдер предзаполняется значением из ссылки). При включении чекбокса количество фиксируется.',
            CREATE_LINK_BLOCK_TARIFF_TYPES.lessonCount,
          )}
        />
      </div>

      <div style={{ paddingLeft: '8px', paddingTop: '4px' }}>
        <FormItem<FieldType> name="is_value_fixed" hidden>
          <input type="hidden" />
        </FormItem>
        <FormItem<FieldType>
          name="value"
          rules={[
            {
              required: true,
              message: 'Укажите количество занятий',
            },
            {
              type: 'number',
              min: 1,
              max: 100,
              message: 'Количество занятий: от 1 до 100',
              transform: (value) => Number(value),
            },
          ]}
          noStyle
        >
          <LessonCountSliderField />
        </FormItem>
      </div>
    </div>
  )
}

export default LessonCountLinkFields
